import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ShieldOff } from "lucide-react";
import { BlacklistTable } from "@/components/blacklist/BlacklistTable";
import { AddTopicDialog } from "@/components/blacklist/AddTopicDialog";
import { ImportTemplateDialog } from "@/components/blacklist/ImportTemplateDialog";

export default function GroupBlacklist() {
  const { groupId } = useParams<{ groupId: string }>();

  const { data: group } = useQuery({
    queryKey: ["group", groupId],
    queryFn: async () => {
      if (!groupId) return null;
      const { data, error } = await supabase
        .from("syra_groups")
        .select("id, name")
        .eq("id", groupId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!groupId,
  });

  if (!groupId) {
    return <p className="p-6 text-sm text-destructive">Grupo não informado.</p>;
  }

  return (
    <div className="container max-w-5xl py-6 space-y-6">
      <div>
        <Button variant="ghost" size="sm" asChild className="mb-2">
          <Link to="/groups">
            <ChevronLeft className="h-4 w-4 mr-1" />
            Voltar para grupos
          </Link>
        </Button>
        <div className="flex items-center gap-3">
          <ShieldOff className="h-6 w-6 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold">Tópicos bloqueados</h1>
            <p className="text-sm text-muted-foreground">
              {group?.name ?? "Grupo"} — assuntos que a SYRA evita neste grupo.
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base">Lista atual</CardTitle>
            <CardDescription>
              Tópicos vindos do template, do botão "Adicionar" ou de comandos
              <span className="font-mono mx-1">"syra blacklist X"</span>
              no WhatsApp aparecem aqui.
            </CardDescription>
          </div>
          <div className="flex gap-2 shrink-0">
            <ImportTemplateDialog groupId={groupId} />
            <AddTopicDialog groupId={groupId} />
          </div>
        </CardHeader>
        <CardContent>
          <BlacklistTable groupId={groupId} />
        </CardContent>
      </Card>
    </div>
  );
}
